/**
 * 로딩 스피너 컴포넌트
 * 운세 분석 중 표시되는 스피너와 안내 메시지
 */
import React from 'react';
import styled, { keyframes } from 'styled-components';
import { tokens } from '../../../design-system/tokens';

const rotate = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${tokens.spacing[10]};
  text-align: center;

  @media (max-width: ${tokens.breakpoints.sm}) {
    padding: ${tokens.spacing[8]};
  }
`;

const SpinnerWrapper = styled.div`
  position: relative;
  width: 60px;
  height: 60px;
  margin-bottom: ${tokens.spacing[6]};

  @media (max-width: ${tokens.breakpoints.sm}) {
    width: 50px;
    height: 50px;
    margin-bottom: ${tokens.spacing[5]};
  }
`;

const Spinner = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border: 4px solid ${tokens.colors.neutral[200]};
  border-radius: 50%;
  border-top-color: ${tokens.colors.primary[500]};
  animation: ${rotate} 1s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite;

  @media (max-width: ${tokens.breakpoints.sm}) {
    border-width: 3px;
  }
`;

const InnerSpinner = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  right: 8px;
  bottom: 8px;
  border: 3px solid ${tokens.colors.neutral[200]};
  border-radius: 50%;
  border-bottom-color: ${tokens.colors.primary[400]};
  animation: ${rotate} 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite reverse;

  @media (max-width: ${tokens.breakpoints.sm}) {
    top: 7px;
    left: 7px;
    right: 7px;
    bottom: 7px;
    border-width: 2px;
  }
`;

const pulse = keyframes`
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    opacity: 1;
  }
`;

const StarIcon = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 20px;
  animation: ${pulse} 2s ease-in-out infinite;

  @media (max-width: ${tokens.breakpoints.sm}) {
    font-size: 18px;
  }
`;

const MainMessage = styled.h3`
  font-family: ${tokens.typography.fontFamily.primary};
  font-size: ${tokens.typography.fontSize.xl};
  font-weight: ${tokens.typography.fontWeight.semibold};
  color: ${tokens.colors.neutral[800]};
  margin-bottom: ${tokens.spacing[3]};
  letter-spacing: -0.7px;

  @media (max-width: ${tokens.breakpoints.sm}) {
    font-size: ${tokens.typography.fontSize.lg};
    margin-bottom: ${tokens.spacing[2]};
  }
`;

const SubMessage = styled.p`
  font-family: ${tokens.typography.fontFamily.primary};
  font-size: ${tokens.typography.fontSize.sm};
  font-weight: ${tokens.typography.fontWeight.regular};
  color: ${tokens.colors.neutral[600]};
  margin-bottom: ${tokens.spacing[2]};
  letter-spacing: -0.6px;
  line-height: ${tokens.typography.lineHeight.relaxed};

  @media (max-width: ${tokens.breakpoints.sm}) {
    font-size: ${tokens.typography.fontSize.xs};
  }
`;

const TimeMessage = styled.p`
  font-family: ${tokens.typography.fontFamily.primary};
  font-size: ${tokens.typography.fontSize.base};
  font-weight: ${tokens.typography.fontWeight.medium};
  color: ${tokens.colors.primary[600]};
  letter-spacing: -0.6px;

  @media (max-width: ${tokens.breakpoints.sm}) {
    font-size: ${tokens.typography.fontSize.sm};
  }
`;

const DotAnimation = styled.span`
  &::after {
    content: '.';
    animation: ${keyframes`
      0% { content: '.'; }
      33% { content: '..'; }
      66% { content: '...'; }
    `} 1.5s infinite;
  }
`;

interface LoadingSpinnerProps {
  message?: string;
}

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  message = "AI가 운세를 분석하고 있습니다"
}) => {
  return (
    <Container>
      <SpinnerWrapper>
        <Spinner />
        <InnerSpinner />
        <StarIcon>✨</StarIcon>
      </SpinnerWrapper>
      <MainMessage>
        {message}<DotAnimation />
      </MainMessage>
      <SubMessage>
        사주팔자와 명리학 원리를 바탕으로 정밀하게 분석 중입니다.
      </SubMessage>
      <TimeMessage>
        ⏱️ 정확한 분석을 위해 1~2분 정도 소요됩니다
      </TimeMessage>
    </Container>
  );
};